import { create } from 'zustand';
import { Customer } from '@/types';
import { authApi, extractResource } from '@/lib/api';
import { getToken, setToken, removeToken } from '@/lib/auth';
import { useCartStore } from '@/store/cartStore';
import { runSignOutResets } from '@/lib/storeResets';
import { registerPushToken, removePushToken } from '@/lib/native';

interface AuthState {
  customer: Customer | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string, customer: Customer) => void;
  logout: () => Promise<void>;
  updateProfile: (customer: Customer) => void;
  checkAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  customer: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,

  login: (token: string, customer: Customer) => {
    setToken(token);
    set({ token, customer, isAuthenticated: true, isLoading: false });
    // Fire-and-forget: merge any guest-cart items the visitor accumulated before
    // signing in. Failures here shouldn't block login — the user can still
    // re-add items manually. Guards against test envs where the cart store
    // hasn't been initialised.
    try {
      void useCartStore.getState().mergeGuestCart();
    } catch {
      // ignore
    }
    // On the native app, register this device for order push notifications.
    // No-op on web; failures are swallowed inside registerPushToken.
    void registerPushToken();
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors on logout
    } finally {
      removeToken();
      // Stop this device receiving the prior user's pushes (native only).
      void removePushToken();
      // Wipe client-side caches that hung off the auth identity. Wrapped in
      // try/catch so a thrown reset can't block sign-out from completing.
      try {
        useCartStore.getState().clearCart();
      } catch {
        // ignore
      }
      // Reset per-user state owned by lazily-loaded stores (the design editor
      // registers its own reset). Importing designStore here would pull Konva
      // into the eager bundle on every page — see lib/storeResets.ts.
      runSignOutResets();
      set({ token: null, customer: null, isAuthenticated: false, isLoading: false });
    }
  },

  updateProfile: (customer: Customer) => {
    set({ customer });
  },

  checkAuth: async () => {
    const token = getToken();
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }
    try {
      const response = await authApi.me();
      set({
        token,
        customer: extractResource<Customer>(response.data, 'customer'),
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      removeToken();
      set({ token: null, customer: null, isAuthenticated: false, isLoading: false });
    }
  },
}));
